import React from "react"
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material"

// Server timestamps are sqlite's datetime('now'): UTC, no zone marker
export const parseServerTime = (text) => new Date(`${text.replace(" ", "T")}Z`)

export const timeAgo = (text, now = new Date()) => {
  const seconds = Math.max(0, (now - parseServerTime(text)) / 1000)
  if (seconds < 60) return "just now"
  const minutes = seconds / 60
  if (minutes < 60) return `${Math.floor(minutes)}m ago`
  const hours = minutes / 60
  if (hours < 24) return `${Math.floor(hours)}h ago`
  const days = hours / 24
  if (days < 30) return `${Math.floor(days)}d ago`
  return parseServerTime(text).toLocaleDateString()
}

// The slot request that puts a history entry back on the wall
export const slotBodyFor = (entry) =>
  entry.kind === "climb"
    ? { type: "climb", climb_uuid: entry.climb_uuid, angle: entry.angle }
    : {
        type: "custom",
        frames: entry.frames,
        name: entry.name,
        angle: entry.angle,
      }

/**
 * The problems most recently shown at a gym, newest first, each with
 * buttons to put it back in slot A or B.
 */
export default function RecentProblems({
  entries,
  isLoading,
  onDisplay,
  onOpen,
}) {
  if (isLoading && !entries) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }
  if (!entries?.length) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{ py: 4 }}
      >
        Nothing has been displayed here yet.
      </Typography>
    )
  }
  return (
    <List dense disablePadding data-testid="recent-problems">
      {entries.map((entry) => {
        const key = entry.climb_uuid || entry.frames
        const canOpen = entry.kind === "climb" && !entry.error && onOpen
        const detail = [
          entry.grade && `${entry.grade} @ ${entry.grade_angle}°`,
          entry.setter_username && `by ${entry.setter_username}`,
          entry.error === "climb_not_found" && "no longer in the database",
          `${timeAgo(entry.displayed_at)} in ${entry.slot}`,
        ]
          .filter(Boolean)
          .join(" · ")
        return (
          <ListItem
            key={key}
            divider
            disableGutters
            secondaryAction={
              <ButtonGroup size="small" variant="outlined">
                {["A", "B"].map((slot) => (
                  <Tooltip key={slot} title={`Show as problem ${slot}`}>
                    <Button
                      aria-label={`Show ${entry.name} as problem ${slot}`}
                      disabled={Boolean(entry.error)}
                      onClick={() => onDisplay(entry, slot)}
                      sx={{ minWidth: 32, px: 1 }}
                    >
                      {slot}
                    </Button>
                  </Tooltip>
                ))}
              </ButtonGroup>
            }
            sx={{ pr: 11 }}
          >
            <ListItemText
              primary={entry.name}
              secondary={detail}
              primaryTypographyProps={{
                noWrap: true,
                fontWeight: 600,
                ...(canOpen && {
                  sx: { cursor: "pointer" },
                  onClick: () => onOpen(entry),
                }),
              }}
              secondaryTypographyProps={{ noWrap: true, variant: "caption" }}
            />
          </ListItem>
        )
      })}
    </List>
  )
}
